import { PT_TIMEZONE } from '../constants/dateTime';
import { formatDateWithOrdinalSuffix } from '../utils';

export const formatEventDateTime = (startIso: string | null | undefined, endIso: string | null | undefined) => {
  if (!startIso && !endIso) return '-';

  const start = new Date(startIso || '');
  const end = new Date(endIso || '');

  const isValid = (d: Date) => !Number.isNaN(d.getTime());
  const hasStart = isValid(start);
  const hasEnd = isValid(end);

  if (!hasStart && !hasEnd) return startIso ?? endIso ?? '-';
  if (hasStart && !hasEnd) return startIso;
  if (!hasStart && hasEnd) return endIso;

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const weekdayDateFmt = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  const startTimeSameDayFmt = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const fullDateTimeFmt = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const formatSameDayStart = (d: Date) => {
    const t = startTimeSameDayFmt.format(d).replace(/\s?(AM|PM)$/i, '');
    return t;
  };

  const formatSameDayEnd = (d: Date) => {
    const t = startTimeSameDayFmt.format(d);
    return t.replace(/\s?(AM|PM)$/i, (_, ap) => ap.toLowerCase());
  };

  if (sameDay(start, end)) {
    return `${weekdayDateFmt.format(start)} ⋅ ${formatSameDayStart(start)} - ${formatSameDayEnd(end)}`;
  }

  const format = (s: string) => s.replace(/\s?(AM|PM)$/i, (_, ap) => ap.toLowerCase());

  return `${format(fullDateTimeFmt.format(start))} - ${format(fullDateTimeFmt.format(end))}`;
};
export const parseDateTime = (value: string | null | undefined): Date | null => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

export const formatDateTime = (
  value: string | null | undefined,
  options?: {
    fallback?: string | null;
    includeSeconds?: boolean;
    withTZ?: boolean;
    month?: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow';
    day?: 'numeric' | '2-digit';
  },
): string | null => {
  const fallback = options?.fallback ?? null;
  const date = parseDateTime(value);
  if (!date) return fallback;

  const fmt: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: options?.month ?? '2-digit',
    day: options?.day ?? '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  if (options?.includeSeconds) fmt.second = '2-digit';
  if (options?.withTZ) fmt.timeZoneName = 'short';

  if (options?.month === 'long' || options?.month === 'short') {
    const dFmt: Intl.DateTimeFormatOptions = {
      year: fmt.year,
      month: fmt.month,
      day: fmt.day,
    };
    const tFmt: Intl.DateTimeFormatOptions = {
      hour: fmt.hour,
      minute: fmt.minute,
      hour12: fmt.hour12,
    };
    if (options?.includeSeconds) tFmt.second = '2-digit';
    if (options?.withTZ) tFmt.timeZoneName = 'short';

    return `${date.toLocaleDateString('en-US', dFmt)}, ${date.toLocaleTimeString('en-US', tFmt)}`;
  }

  return date.toLocaleString('en-US', fmt);
};

const reportDateFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: PT_TIMEZONE,
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const reportTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: PT_TIMEZONE,
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

export const getOrdinalSuffix = (day: number): string =>
  ['th', 'st', 'nd', 'rd'][(day % 100 > 10 && day % 100 < 20) ? 0 : (day % 10 < 4 ? day % 10 : 0)];

export const formatReportDate = (dateString: string, includeTime = false): string => {
  const strVal = dateString.trim();

  if (strVal.includes(':')) {
    const cleanStr = strVal.replace(/\s+(?:UTC|GMT)$/i, 'Z');
    const isoTimestamp = cleanStr.includes('T')
      ? cleanStr
      : cleanStr.replace(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)/, '$1T$2');
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(isoTimestamp);
    const date = new Date(hasTimezone ? isoTimestamp : `${isoTimestamp}Z`);

    const dateParts = reportDateFormatter.formatToParts(date);
    const month = dateParts.find(({ type }) => type === 'month')!.value;
    const day = Number(dateParts.find(({ type }) => type === 'day')!.value);
    const year = dateParts.find(({ type }) => type === 'year')!.value;
    const suffix = getOrdinalSuffix(day);

    if (includeTime) {
      const time = reportTimeFormatter.format(date);
      return `${month} ${day}${suffix}, ${year}, ${time} PT`;
    }

    return `${month} ${day}${suffix}, ${year}`;
  }

  return formatDateWithOrdinalSuffix(strVal, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};
